import { useMemo, useState, type ReactElement } from 'react';
import { minutes, type ActualWorkEntryInput, type ResourceId, type TaskId } from '@bigint-project/core';
import { listActualWorkEntries, taskProgressMode } from '@bigint-project/core';

import { TimesheetGrid, type TimesheetGridRow } from '../components/timesheet';
import {
  progressAsOf,
  selectExpectedProgressByTask,
  selectIsWorkingDay,
  selectProjectProgressSummary,
} from '../integration/derivations';
import { useProjectStore } from '../store/project-store';
import { EmptyState } from './EmptyState';

const MS_PER_DAY = 86_400_000;

/** `YYYY-MM-DD` ± n 天（以 UTC 計算，避免夏令時間讓日期跳格）。 */
function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * MS_PER_DAY).toISOString().slice(0, 10);
}

/** 該日所屬週的週一。 */
function startOfWeek(date: string): string {
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
  return addDays(date, weekday === 0 ? -6 : 1 - weekday);
}

/**
 * 資源逐日工時回報檢視。
 *
 * 一次編輯 = 一次 `recordActualWork`，且送出的是**該資源本週的完整 entries**：
 * core 的工時表是實際工時的唯一真相（整批重填而非累加），只送被改動的那一格
 * 會讓同週其他天的回報在回填時消失。
 */
export function TimesheetView({ readOnly = false }: { readonly readOnly?: boolean }): ReactElement {
  const project = useProjectStore((state) => state.project);
  const recordActualWork = useProjectStore((state) => state.recordActualWork);
  const selectTasks = useProjectStore((state) => state.selectTasks);
  const openTaskProgress = useProjectStore((state) => state.openTaskProgress);

  const asOf = progressAsOf(project);
  const workResources = useMemo(
    () => project.resources.filter((resource) => resource.type === 'Work' && resource.isBudget !== true),
    [project.resources],
  );
  const [resourceId, setResourceId] = useState<ResourceId | null>(null);
  const [weekStart, setWeekStart] = useState(() => startOfWeek((asOf as string).slice(0, 10)));
  const [error, setError] = useState<string | null>(null);

  const activeResourceId = resourceId ?? workResources[0]?.id ?? null;
  const dates = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  );
  const isWorkingDay = selectIsWorkingDay(project);
  const nonWorkingDates = useMemo(
    () => new Set(dates.filter((date) => !isWorkingDay(date))),
    [dates, isWorkingDay],
  );
  const expectedByTask = selectExpectedProgressByTask(project, asOf);
  const summary = selectProjectProgressSummary(project, asOf);

  const parentIds = useMemo(
    () => new Set(project.tasks.flatMap((task) => (task.parentId === null ? [] : [task.parentId as string]))),
    [project.tasks],
  );
  const tasksById = useMemo(
    () => new Map(project.tasks.map((task) => [task.id as string, task])),
    [project.tasks],
  );

  /** assignmentId → date → { hours, notes }；整週送出時要把備註原樣帶回。 */
  const entriesByAssignment = useMemo(() => {
    const byAssignment = new Map<string, Map<string, { hours: number; notes: string }>>();
    if (activeResourceId === null) return byAssignment;
    for (const entry of listActualWorkEntries(project, { resourceId: activeResourceId })) {
      if (entry.assignmentId === null) continue;
      const key = entry.assignmentId as string;
      const byDate = byAssignment.get(key) ?? new Map<string, { hours: number; notes: string }>();
      byDate.set(entry.date as string, { hours: (entry.actualWork as number) / 60, notes: entry.notes });
      byAssignment.set(key, byDate);
    }
    return byAssignment;
  }, [activeResourceId, project]);

  const rows: readonly TimesheetGridRow[] = useMemo(() => {
    if (activeResourceId === null) return [];
    return project.assignments.flatMap((assignment): readonly TimesheetGridRow[] => {
      if (assignment.resourceId !== activeResourceId) return [];
      const task = tasksById.get(assignment.taskId as string);
      if (task === undefined || task.isSummary || parentIds.has(task.id as string)) return [];
      const byDate = entriesByAssignment.get(assignment.id as string);
      const expected = expectedByTask.get(task.id as string);
      return [{
        assignmentId: assignment.id,
        taskId: task.id,
        wbs: task.wbs,
        taskName: task.name,
        progressMode: taskProgressMode(task),
        status: expected?.status ?? 'notStarted',
        expectedPercent: (expected?.expectedPercent as number | undefined) ?? 0,
        percentComplete: task.percentComplete as number,
        totalActualHours: (assignment.actualWork as number) / 60,
        remainingHours: (assignment.remainingWork as number) / 60,
        cells: dates.map((date) => ({ date, hours: byDate?.get(date)?.hours ?? 0 })),
      }];
    });
  }, [activeResourceId, dates, entriesByAssignment, expectedByTask, parentIds, project.assignments, tasksById]);

  const commitCell = (assignmentId: TimesheetGridRow['assignmentId'], date: string, hours: number): void => {
    if (readOnly || activeResourceId === null) return;
    const entries: ActualWorkEntryInput[] = [];
    for (const row of rows) {
      const byDate = entriesByAssignment.get(row.assignmentId as string);
      for (const day of dates) {
        const existing = byDate?.get(day);
        const isEdited = row.assignmentId === assignmentId && day === date;
        if (existing === undefined && !isEdited) continue;
        const value = isEdited ? hours : existing?.hours ?? 0;
        entries.push({
          date: day as ActualWorkEntryInput['date'],
          taskId: row.taskId,
          assignmentId: row.assignmentId,
          actualWork: minutes(Math.round(value * 60)),
          notes: existing?.notes ?? '',
        });
      }
    }
    if (entries.length === 0) return;
    const result = recordActualWork({ resourceId: activeResourceId, entries });
    setError(result.ok ? null : result.error.message);
  };

  const openProgress = (taskId: TaskId): void => {
    selectTasks([taskId as string]);
    openTaskProgress();
  };

  if (workResources.length === 0) {
    return (
      <EmptyState
        title="尚無工作資源"
        description="先在資源工作表建立 Work 資源，並指派到任務，這裡才會有工時可回報。"
        testId="timesheet-empty"
      />
    );
  }

  return (
    <div data-testid="timesheet-view" className="flex h-full w-full flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label className="flex items-center gap-1">
          資源
          <select
            aria-label="回報資源"
            value={(activeResourceId ?? '') as string}
            onChange={(event) => setResourceId(event.target.value as ResourceId)}
            className="rounded border border-line bg-transparent px-1 py-0.5"
          >
            {workResources.map((resource) => (
              <option key={resource.id as string} value={resource.id as string}>{resource.name}</option>
            ))}
          </select>
        </label>
        <button type="button" onClick={() => setWeekStart((current) => addDays(current, -7))} className="rounded border border-line px-2 py-0.5">上一週</button>
        <button type="button" onClick={() => setWeekStart(startOfWeek((asOf as string).slice(0, 10)))} className="rounded border border-line px-2 py-0.5">本週</button>
        <button type="button" onClick={() => setWeekStart((current) => addDays(current, 7))} className="rounded border border-line px-2 py-0.5">下一週</button>
        <span data-testid="timesheet-week-range" className="text-fg-muted">{weekStart} ~ {addDays(weekStart, 6)}</span>
        <span
          data-testid="timesheet-progress-summary"
          className={[
            'ml-auto rounded px-2 py-0.5',
            summary.counts.behind > 0 ? 'bg-danger-soft text-danger' : 'bg-raised text-fg-muted',
          ].join(' ')}
        >
          落後 {summary.counts.behind} ・ 進行中 {summary.counts.onTrack + summary.counts.ahead} ・ 完成 {summary.counts.complete}
        </span>
      </div>

      {error === null ? null : (
        <p role="alert" data-testid="timesheet-error" className="rounded bg-danger-soft px-2 py-1 text-xs text-danger">{error}</p>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title="這位資源沒有可回報的指派"
          description="把資源指派到葉節點任務後，這裡會出現逐日工時格。"
          testId="timesheet-no-assignments"
        />
      ) : (
        <div className="min-h-0 flex-1 rounded border border-line">
          <TimesheetGrid
            dates={dates}
            nonWorkingDates={nonWorkingDates}
            rows={rows}
            readOnly={readOnly}
            onCellCommit={commitCell}
            onOpenTaskProgress={openProgress}
          />
        </div>
      )}
    </div>
  );
}
